import React, { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
//components
import {
  Button,
  Sheet,
  SearchInput,
  SearchSmIcon,
  IconButton,
  DeclineIcon,
} from '@paypalcorp/pp-react'
import LinkResultsSection from './LinkResultsSection'
import AiAnswerSummary from './AiAnswerSummary'
//styles
import styles from './AiSearch.module.css'
import axios from 'axios'
import SSE from '../utils/sse'
import { useWindowSize } from '../../../../hooks'
import { css } from '@emotion/react'
import PrivacyPolicy from './PrivacyPolicy'

interface SearchButtonProps {
  onClick: () => void
}
const SearchButton = ({ onClick }: SearchButtonProps) => {
  const windowSize = useWindowSize()

  return (
    <Button
      secondary
      onClick={onClick}
      iconComponent={SearchSmIcon}
      className={styles.searchLaunchButton}
    >
      {windowSize.width! <= 1350 ? 'Search' : 'What are you looking for?'}
    </Button>
  )
}
interface AiSearchProps {
  searchID: string
  setSearchID: (searchID: string) => void
}
const AiSearch = ({ setSearchID, searchID }: AiSearchProps) => {
  const [showForm, setShowForm] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [answerSummary, setAnswerSummary] = useState('')
  const [aiSummaryErrorStatus, setAiSummaryErrorStatus] = useState('')
  const [isLoadingSummary, setIsSummaryLoading] = useState(false)
  const [linkResults, setLinkResults] = useState([])
  const [linkResultsErrorStatus, setLinkResultsErrorStatus] = useState('')
  const [isLoadingLinkResults, setIsLoadingLinkResults] = useState(false)
  const [isStreamingComplete, setIsStreamingComplete] = useState(true)
  const [page, setPage] = useState(1)
  const fptiData = typeof window !== `undefined` && window.fpti
  let isLoadingHandled = false
  let typeWriterDelay = 0
  const typeWriterIncrementValue = 70

  const fptiOptions = {
    page: fptiData && fptiData.page,
    pgrp: fptiData && fptiData.pgrp,
    component: 'devdiscoverynodeweb',
    name: 'gen_ai_summary',
    query: searchQuery,
    searchID,
  }

  const initialState = {
    streamingStarted: false,
    aiStreamLoading: true,
    isStreamError: false, // to handle errors
    answerId: '', // answerId for FPTI purpose
  }
  const [aiResultState, setAiResultState] = useState(initialState)

  // To handle streaming error and its FPTI
  const handleStreamError = (statusCode: number, error) => {
    if (fptiData) {
      const data = {
        ...fptiOptions,
        hc_event_info: error,
        link: `gen-ai-stream-error|${statusCode}|${error}`,
        tag: 'gen-ai-stream-error',
      }

      if (window.PAYPAL?.analytics?.instance) {
        window.PAYPAL?.analytics?.instance?.recordError({ data })
      }
    }

    setAiSummaryErrorStatus(statusCode > 0 ? statusCode : '')
  }

  const fetchSummaryResults = async (text: string, uuid: string) => {
    try {
      setIsSummaryLoading(true)
      // initiate streaming API response
      const initiateAiSearchStream = () => {
        if (fptiData) {
          const data = {
            ...fptiOptions,
            searchID: uuid || searchID,
          }
          if (window.PAYPAL?.analytics?.instance) {
            window.PAYPAL?.analytics?.instance?.recordImpression({ data })
          }
        }
        const source = new SSE('/dashboard/api/ai/gen-ai-qna', {
          headers: {
            'Content-Type': 'application/json',
          },
          payload: JSON.stringify({
            query: text,
            // _csrf: csrf,
          }),
          withCredentials: false,
        })

        let totalPayloadLength = 0

        source.addEventListener('message', function (event) {
          // Assuming we receive JSON-encoded data payloads:
          const data = JSON.parse(event.data)
          const payload = data.payload || ''
          const answerId = data?.answerId || null
          if (data.operation !== 'end') {
            totalPayloadLength = totalPayloadLength + payload.length
          }
          // Error Case
          if (data.operation === 'error') {
            // Setting statuscode as 500 in FPTI so that we can know these errors are from server
            return handleStreamError(500, payload)
          }

          // END Case - on streaming end, enable feedback section
          if (data.operation === 'end') {
            if (payload.length > 1) {
              if (fptiData) {
                const data = {
                  ...fptiOptions,
                  type: 'success',
                  event_name: 'streaming-complete',
                  tag: 'streaming-complete',
                  answerId: `${answerId || ''}`,
                  searchID: uuid || searchID,
                }
                if (window.PAYPAL?.analytics?.instance) {
                  window.PAYPAL?.analytics?.instance?.recordImpression({ data })
                }
              }
              isLoadingHandled = false
              return
            }
            isLoadingHandled = false
            return handleStreamError(204, 'streaming-ended-with-no-content')
          }

          // Sucess Case
          const chunkDelayTimeout = setTimeout(() => {
            setAnswerSummary((prevText) => {
              const tempAnswerSummary = prevText + `${payload}`
              if (tempAnswerSummary.length === totalPayloadLength) {
                // if the payload fully set
                setIsStreamingComplete(true)
              }
              return tempAnswerSummary
            })
            return () => {
              clearTimeout(chunkDelayTimeout)
            }
          }, typeWriterDelay)
          typeWriterDelay += typeWriterIncrementValue

          //Streaming Started Event for first chunk
          if (!isLoadingHandled && payload.length >= 1) {
            setIsSummaryLoading(false)
            setIsStreamingComplete(false)
            isLoadingHandled = true

            if (fptiData) {
              const data = {
                ...fptiOptions,
                type: 'success',
                tag: 'streaming-started',
                event_name: 'streaming-started',
                answerId: `${answerId || ''}`,
                searchID: uuid || searchID,
              }

              if (window.PAYPAL?.analytics?.instance) {
                window.PAYPAL?.analytics?.instance?.recordImpression({ data })
              }
            }
            return null
          }
        })

        // on streaming error
        source.addEventListener('error', function (event) {
          // added try catch block to handle unexpected error formats and not to break the UI
          try {
            // Assuming we receive JSON-encoded data payloads:
            const data = JSON.parse(event.data)
            const { status } = data
            let errorMsg = 'event-stream-error'
            if (status === 404) {
              errorMsg = 'data-not-found'
            }
            handleStreamError(status, errorMsg)
          } catch (e) {
            handleStreamError(422, 'event-stream-error-listener-catch')
          }
        })

        source.stream()
      }

      initiateAiSearchStream()
    } catch (error) {
      console.log('error', error)
      setAiSummaryErrorStatus(error)
    }
  }

  const fetchLinkResults = async (text: string) => {
    if (!text) {
      return
    }
    try {
      setIsLoadingLinkResults(true)

      const LinksResponse = await axios('/dashboard/api/ai/ai-search', {
        method: 'POST',
        data: {
          query: text,
          page,
          page_size: 10,
        },
      })
      const rankedEntries = LinksResponse.data.ranked_entries || []

      setLinkResults((prevItems) => [...prevItems, ...rankedEntries])
      setPage((prevPage) => prevPage + 1)
    } catch (error) {
      console.log('error', error)
      setLinkResultsErrorStatus(error)
      setPage(1)
    } finally {
      setIsLoadingLinkResults(false)
    }
  }
  const fetchResults = (text: string, uuid: string) => {
    setAnswerSummary('')
    setLinkResults([])
    fetchSummaryResults(text, uuid)
    fetchLinkResults(text)
  }
  const fetchResultsOnEnter = (e) => {
    if (searchQuery.length <= 100) {
      setPage(1)
      setAiSummaryErrorStatus('')
      setLinkResultsErrorStatus('')
      let uuid = searchID
      if (e.key === 'Enter') {
        uuid = uuidv4()
        setSearchID(uuid)

        if (fptiData) {
          const data = {
            ...fptiOptions,
            link: 'onEnter_search',
            e: 'keypress',
            tag: 'onEnter_search',
            event_action: 'onEnter_search',
            event_name: 'Click on Enter button',
            searchID: uuid,
          }

          if (window.PAYPAL?.analytics?.instance) {
            window.PAYPAL?.analytics?.instance?.record({ data })
          }
        }
        fetchResults(searchQuery, uuid)
      }
    }
  }

  const handleChange = (evt: React.BaseSyntheticEvent) => {
    setSearchQuery(evt.target.value)
  }

  const handleClear = () => {
    if (fptiData) {
      const data = {
        ...fptiOptions,
        link: 'clear-search-input',
        tag: 'clear-search-input',
      }

      if (window.PAYPAL?.analytics?.instance) {
        window.PAYPAL?.analytics?.instance?.recordClick({ data })
      }
    }
    setSearchQuery('')
    setPage(1)
  }
  const setSearchText = (searchText: string) => {
    setSearchQuery(searchText)
  }
  const openSheet = () => {
    if (fptiData) {
      const uuid = uuidv4()
      setSearchID(uuid)

      const data: {
        [key: string]: any
      } = {
        ...fptiOptions,
        link: 'open_search_modal',
        tag: 'open_search_modal',
      }
      const { searchID, ...clickDataToLog } = data
      if (window.PAYPAL?.analytics?.instance) {
        window.PAYPAL?.analytics?.instance?.recordClick({
          data: clickDataToLog,
        })
      }
    }
    setShowForm(true)
    setPage(1)
  }
  const closeSheet = () => {
    if (fptiData) {
      const data: {
        [key: string]: any
      } = {
        ...fptiOptions,
        link: 'close_search_modal',
        tag: 'close_search_modal',
      }
      const { searchID, ...clickDataToLog } = data

      if (window.PAYPAL?.analytics?.instance) {
        window.PAYPAL?.analytics?.instance?.recordClick({
          data: clickDataToLog,
        })
      }
    }
    setShowForm(false)
    setPage(1)
  }

  const RightIcon = () => {
    return (
      <div className={styles.rightIconContainer}>
        <div className={styles.charCountContainer}>
          {`${searchQuery.length}/100`}
        </div>
        <div>
          <IconButton
            buttonType="tertiary"
            svgIconComponent={DeclineIcon}
            onClick={() => setSearchQuery('')}
            onMouseDown={(evt: any) => evt.preventDefault()} // remove focus on click
          >
            x
          </IconButton>
        </div>
      </div>
    )
  }

  const rightContainerIconStyles = css`
    width: auto;
    color: ${searchQuery.length > 100 && 'red'};
  `

  return (
    <>
      <SearchButton onClick={openSheet} />
      {showForm && (
        <Sheet
          title="search results"
          hideTitle={true}
          isOpen={showForm}
          onCloseComplete={closeSheet}
          showLogo={true}
          className={styles.modalContainer}
        >
          <SearchInput
            label="What are you looking for?"
            name="search"
            value={searchQuery}
            onClear={handleClear}
            onChange={handleChange}
            onKeyDown={fetchResultsOnEnter}
            className={styles.searchRow}
            error={searchQuery.length > 100}
            errorText={
              searchQuery.length > 100 && 'Enter 100 characters or less'
            }
            rightIcon={<RightIcon />}
            rightIconCSS={rightContainerIconStyles}
            helperText={PrivacyPolicy}
            disabled={!isStreamingComplete}
          />

          <AiAnswerSummary
            handleSuggestionClick={setSearchText}
            isLoadingSummary={isLoadingSummary}
            fetchResults={fetchResults}
            answerSummary={answerSummary}
            errorStatus={aiSummaryErrorStatus}
            setSearchQuery={setSearchQuery}
            searchQuery={searchQuery}
            answerId={aiResultState.answerId}
            searchID={searchID}
            isStreamingComplete={isStreamingComplete}
          />
          <LinkResultsSection
            handleSuggestionClick={setSearchText}
            isLoadingLinkResults={isLoadingLinkResults}
            errorStatus={linkResultsErrorStatus}
            fetchResults={fetchResults}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            fetchLinkResults={fetchLinkResults}
            linkResults={linkResults}
            answerId={aiResultState.answerId}
            searchID={searchID}
            page={page}
          />
        </Sheet>
      )}
    </>
  )
}

export default AiSearch
